'use client';
import React from 'react';
import Link from '@/core/i18n/navigation';
import { PlusCircle, Image as ImageIcon, Pause, Play } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { BlockProps } from './types';
import { getPublicMediaUrl, isUnresolvedPlaceholder } from '@/core/utils/media';
import { toCssAspectRatio } from '@/core/utils/aspectRatio';
import { ResponsiveVideo } from './ResponsiveVideo';
import { ResponsiveImage, lcpImagePriority, type ImageLoadPriority } from '../shared/ResponsiveImage';
import { ALL_BREAKPOINTS, type Breakpoint } from '../breakpoints';
import { formatStyle } from '../blockHelpers';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { useRegisterHeaderTransparency } from '@/core/components/providers/HeaderTransparencyContext';

const isVideoPath = (url: string) => /\.(mp4|webm|mov)(\?.*)?$/i.test(url);

function toCssLength(value: unknown, fallback = '0px'): string {
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}px`;
  if (typeof value === 'string' && value.trim()) {
    const trimmed = value.trim();
    return /^-?\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
  }
  return fallback;
}

function getSliderFrameDimensions(styles: any) {
  const aspectRatio = toCssAspectRatio(styles?.aspectRatio);
  return {
    aspectRatio,
    height: aspectRatio === 'auto' ? toCssLength(styles?.height, '500px') : 'auto',
    minHeight: aspectRatio !== 'auto' ? toCssLength(styles?.height) : '0px',
  };
}

/**
 * Slide background image. ResponsiveImage serves external URLs/data-URIs as-is (they're not on the
 * optimizer's allow-list) and, with `mobileSrc`, renders one <picture> so only the variant for the
 * current viewport is downloaded. className carries the `absolute inset-0 w-full h-full` sizing.
 */
function SlideBgImage({
  src, mobileSrc, alt, priority, preloadMedia, sizes, className, style,
}: {
  src: string; mobileSrc?: string; alt?: string; priority: ImageLoadPriority; preloadMedia?: string; sizes: string; className: string; style?: React.CSSProperties;
}) {
  return (
    <ResponsiveImage
      src={src}
      mobileSrc={mobileSrc}
      alt={alt || ''}
      sizes={sizes}
      priority={priority}
      preloadMedia={preloadMedia}
      className={cn(className, 'slide-bg-media')}
      style={style}
    />
  );
}

// Slow zoom on every slide photo. It runs continuously (alternate) rather than restarting per
// slide, so a slide fading out never snaps back to scale 1 mid-transition. Transform only — no CLS.
const KEN_BURNS_CSS = `@keyframes slider-ken-burns{from{transform:scale(1)}to{transform:scale(1.12)}}
.slider-ken-burns .slide-bg-media{animation:slider-ken-burns var(--slider-kb-duration,10s) ease-in-out infinite alternate;transform-origin:center}
@media (prefers-reduced-motion: reduce){.slider-ken-burns .slide-bg-media{animation:none}}`;

interface SliderBlockProps extends BlockProps {
  activeSlides: Record<string, number>;
  setActiveSlides: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  pausedSliders?: Record<string, boolean>;
  toggleSliderPause?: (id: string) => void;
}

export function SliderBlock({
  block,
  currentStyles,
  viewport,
  isAdmin,
  isBuilderCanvas,
  selectedBlockId,
  onSelectBlock,
  product,
  onUpdateBlockStyle,
  activeSlides,
  setActiveSlides,
  pausedSliders,
  toggleSliderPause,
  depth = 0,
  onDuplicateBlock,
  onMoveBlockUp,
  onMoveBlockDown,
  onRemoveBlock,
  isFirstBlock,
  firstBlockBreakpoints,
}: SliderBlockProps) {
  const { BlockRenderer } = require('../BlockRenderer');
  const { t } = useTranslation();
  const headerTransparent = block.settings?.headerTransparent === true;
  const headerTextColor = block.settings?.headerTextColor || '#ffffff';
  useRegisterHeaderTransparency(
    !isAdmin && isFirstBlock && headerTransparent
      ? { enabled: true, textColor: headerTextColor, ...(block.settings?.headerLogoWhite === true ? { logoWhite: true } : {}) }
      : null
  );

  // 'overlay' (default) keeps arrows floating on top of the slide, which can
  // collide with the photo underneath; 'below' moves them into a static row
  // alongside the dots underneath the slider; 'sides' flanks the slider box
  // itself (outside its left/right edges) so arrows never touch the photo
  // but still sit at the slider's own vertical center, not down at the dots.
  const arrowPosition: 'overlay' | 'below' | 'sides' =
    block.settings?.arrowPosition === 'below' ? 'below'
    : block.settings?.arrowPosition === 'sides' ? 'sides'
    : 'overlay';
  const hasArrows = (block.children?.length || 0) > 1 && !block.settings?.hideArrows;
  const hasDots = block.settings?.showDots && (block.children?.length || 0) > 1;
  const isFade = block.settings?.transitionType === 'fade';
  const kenBurns = Boolean(block.settings?.kenBurns);
  const transitionMs = block.settings?.transitionDuration ?? 500;
  const baseStyles = block.styles || {};
  const mobileFrame = getSliderFrameDimensions({ ...baseStyles, ...(baseStyles.mobile || {}) });
  const tabletFrame = getSliderFrameDimensions({ ...baseStyles, ...(baseStyles.tablet || {}) });
  const desktopFrame = getSliderFrameDimensions({ ...baseStyles, ...(baseStyles.desktop || {}) });
  const storefrontFrameVars = {
    '--slider-mobile-aspect': mobileFrame.aspectRatio,
    '--slider-mobile-height': mobileFrame.height,
    '--slider-mobile-min-height': mobileFrame.minHeight,
    '--slider-tablet-aspect': tabletFrame.aspectRatio,
    '--slider-tablet-height': tabletFrame.height,
    '--slider-tablet-min-height': tabletFrame.minHeight,
    '--slider-desktop-aspect': desktopFrame.aspectRatio,
    '--slider-desktop-height': desktopFrame.height,
    '--slider-desktop-min-height': desktopFrame.minHeight,
  } as React.CSSProperties;

  const renderArrowButton = (direction: 'prev' | 'next', overlay: boolean) => {
    const arrowSize = currentStyles?.arrowSize ?? block.settings?.arrowSize ?? 40;
    const chevronSize = Math.round(arrowSize * 0.3);
    // Glass-on-photo defaults only make sense floating over an image — once
    // arrows sit below the slider on the ordinary page background, unset
    // colors default to a plain neutral button instead of invisible white.
    const arrowStyle: React.CSSProperties = {
      width: `${arrowSize}px`,
      height: `${arrowSize}px`,
      backgroundColor: block.settings?.arrowBg || (overlay ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6'),
      borderColor: block.settings?.arrowBorderColor || (overlay ? 'rgba(255, 255, 255, 0.2)' : '#e5e7eb'),
      color: block.settings?.arrowColor || (overlay ? '#ffffff' : '#171717'),
    };
    const isPrev = direction === 'prev';
    return (
      <button
        aria-label={isPrev ? t('pagination.prev_slide', 'Poprzedni slajd') : t('pagination.next_slide', 'Następny slajd')}
        className={cn(
          'rounded-full backdrop-blur-md border flex items-center justify-center hover:scale-110 transition-all',
          overlay ? cn('absolute top-1/2 -translate-y-1/2 z-20', isPrev ? 'left-4' : 'right-4') : 'relative shrink-0'
        )}
        style={arrowStyle}
        onClick={(e) => {
          e.stopPropagation();
          setActiveSlides((prev) => ({
            ...prev,
            [block.id]: isPrev
              ? Math.max(0, (prev[block.id] || 0) - 1)
              : Math.min(block.children.length - 1, (prev[block.id] || 0) + 1),
          }));
        }}
      >
        <div
          className={cn('border-t-2', isPrev ? 'border-l-2 rotate-[-45deg] ml-1' : 'border-r-2 rotate-[45deg] mr-1')}
          style={{ width: `${chevronSize}px`, height: `${chevronSize}px`, borderColor: 'currentColor' }}
        />
      </button>
    );
  };

  const renderDots = (overlay: boolean) => {
    const vertical = overlay && block.settings?.dotLayout === 'vertical-right';
    const numbered = block.settings?.dotStyle === 'numbers';
    return (
    <div
      className={cn(
        'flex',
        overlay && !vertical && 'absolute left-1/2 -translate-x-1/2 z-20',
        vertical && 'absolute right-5 top-1/2 -translate-y-1/2 z-20 flex-col',
        !overlay && 'relative items-center',
        block.settings?.hideDotsMobile && 'hidden sm:flex'
      )}
      style={{
        bottom: overlay && !vertical ? `${currentStyles?.dotBottom ?? block.settings?.dotBottom ?? 16}px` : undefined,
        gap: `${currentStyles?.dotGap ?? block.settings?.dotGap ?? 8}px`,
      }}
    >
      {block.children.map((_: any, dotIdx: number) => {
        const isActive = (activeSlides[block.id] || 0) === dotIdx;
        const dotSize = currentStyles?.dotSize ?? block.settings?.dotSize ?? 8;
        const hitAreaSize = Math.max(dotSize, 24);
        // Opt-in: an active dot can grow into a pill instead of just
        // scaling up in place — off by default (0) to keep old slides
        // looking exactly as they did before this setting existed.
        const dotActiveWidth = currentStyles?.dotActiveWidth || block.settings?.dotActiveWidth || 0;
        const isPillActive = isActive && dotActiveWidth > 0;
        return (
          <button
            key={dotIdx}
            aria-label={t('slider.goto_slide', 'Przejdź do slajdu {n}').replace('{n}', String(dotIdx + 1))}
            className="flex items-center justify-center cursor-pointer"
            style={{
              width: `${Math.max(hitAreaSize, isPillActive ? dotActiveWidth : 0)}px`,
              height: `${hitAreaSize}px`,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setActiveSlides((prev) => ({ ...prev, [block.id]: dotIdx }));
            }}
          >
            {numbered ? (
              <span
                className="block font-semibold tabular-nums transition-all"
                style={{
                  color: isActive
                    ? (block.settings?.dotActiveColor || (overlay ? '#ffffff' : '#171717'))
                    : (block.settings?.dotColor || (overlay ? 'rgba(255, 255, 255, 0.55)' : '#9ca3af')),
                  fontSize: `${Math.max(10, dotSize)}px`,
                  transform: isActive ? 'scale(1.2)' : 'none',
                }}
              >
                {String(dotIdx + 1).padStart(2, '0')}
              </span>
            ) : (
              <span
                className="transition-all block"
                style={{
                  width: `${isPillActive ? dotActiveWidth : dotSize}px`,
                  height: `${dotSize}px`,
                  borderRadius: isPillActive ? `${dotSize / 2}px` : '9999px',
                  backgroundColor: isActive
                    ? (block.settings?.dotActiveColor || (overlay ? '#ffffff' : '#171717'))
                    : (block.settings?.dotColor || (overlay ? 'rgba(255, 255, 255, 0.4)' : '#d1d5db')),
                  transform: (isActive && !isPillActive) ? 'scale(1.25)' : 'none',
                }}
              />
            )}
          </button>
        );
      })}
    </div>
    );
  };

  return (
    <div
      className={cn(
        'w-full relative group/slider',
        !block.children?.length && 'min-h-[200px]'
      )}
    >
      {!block.children || block.children.length === 0 ? (
        <div
          className="w-full aspect-[21/9] bg-gray-900 rounded-2xl flex items-center justify-center relative overflow-hidden shadow-2xl my-8 group/add-slot"
          onClick={(e) => {
            if (isAdmin && (window as any).__prepareAdd) {
              e.stopPropagation();
              (window as any).__prepareAdd(block.id);
            }
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <div className="flex flex-col items-center justify-center text-white/50 group-hover/add-slot:text-white transition-colors cursor-pointer z-10 p-8 text-center">
            <PlusCircle className="w-12 h-12 mb-4 opacity-50 group-hover/add-slot:opacity-100 transition-opacity" />
            <p className="font-bold tracking-widest uppercase text-xs">{t('slider.empty_title', 'Pusty Slider')}</p>
            <p className="text-[10px] mt-2 opacity-70">{t('slider.empty_hint', 'Kliknij aby dodać pierwszy slajd')}</p>
          </div>
        </div>
      ) : (
        <div className={cn(arrowPosition === 'sides' && 'flex items-center gap-3')}>
        {arrowPosition === 'sides' && renderArrowButton('prev', false)}
        <div className={cn(arrowPosition === 'sides' && 'flex-1 min-w-0')}>
        <div
          className={cn('relative w-full overflow-hidden', !isAdmin && 'storefront-slider-frame', kenBurns && 'slider-ken-burns')}
          style={{
            ...(isAdmin ? getSliderFrameDimensions(currentStyles) : storefrontFrameVars),
            // The block's corner radius must clip the slides: this frame is the element with overflow:hidden.
            ...(currentStyles.borderRadius ? { borderRadius: formatStyle(currentStyles.borderRadius) } : {}),
            ...(kenBurns ? { '--slider-kb-duration': `${Math.max(4000, parseInt(block.settings?.autoplaySpeed) || 5000) * 2}ms` } : {}),
          } as React.CSSProperties}
        >
          {kenBurns && <style>{KEN_BURNS_CSS}</style>}
          {/* Slide rail: translated strip, or (fade) all slides stacked in one grid cell */}
          <div
            className={isFade ? 'grid h-full' : 'flex h-full'}
            style={isFade ? undefined : {
              transform: `translateX(-${(activeSlides[block.id] || 0) * 100}%)`,
              transition: `transform ${transitionMs}ms ease-in-out`,
              willChange: 'transform',
            }}
          >
            {block.children.map((child: any, slideIndex: number) => {
              // contentAlign only ever has desktop/mobile keys (SlideEditor has no separate
              // tablet control) — the real storefront's only breakpoint here groups tablet
              // with desktop (see the sm:-based classes below), so the admin's "Tablet"
              // preview must read the desktop value too, not silently fall back to 'center'.
              const align = child.settings?.contentAlign?.[viewport === 'tablet' ? 'desktop' : viewport] || 'center';
              // Fallback to styles.backgroundImage for slides saved before the wrapper
              // stopped rendering it as CSS (see ImageBlock.tsx for the same migration) —
              // the generic "Tło — obraz" picker was the only one many merchants found.
              const slideContent = child.content || child.styles?.backgroundImage || child.styles?.[viewport]?.backgroundImage;
              const bgImage = isUnresolvedPlaceholder(slideContent) ? '' : slideContent;
              const mobileBgImage = isUnresolvedPlaceholder(child.settings?.mobileImage) ? '' : child.settings?.mobileImage;
              const activeBgImage = (viewport === 'mobile' && mobileBgImage) ? mobileBgImage : bgImage;
              const desktopPoster = isUnresolvedPlaceholder(child.settings?.posterImage) ? '' : child.settings?.posterImage;
              const mobilePoster = isUnresolvedPlaceholder(child.settings?.mobilePosterImage) ? '' : child.settings?.mobilePosterImage;
              const activePoster = viewport === 'mobile' ? (mobilePoster || desktopPoster) : desktopPoster;
              // The first slide is the only one promoted in the server HTML, so later slides do
              // not compete with the initial LCP request. When a manual/autoplay transition makes
              // another slide visible, promote that image before it can become a later LCP entry.
              const isPrioritySlide = isFirstBlock === true && (
                slideIndex === 0 || slideIndex === (activeSlides[block.id] || 0)
              );
              const isActiveSlide = slideIndex === (activeSlides[block.id] || 0);
              const advanceOnVideoEnd = Boolean(block.settings?.advanceOnVideoEnd);
              const handleVideoEnded = advanceOnVideoEnd && isActiveSlide
                ? () => setActiveSlides((previous) => ({
                    ...previous,
                    [block.id]: (slideIndex + 1) % block.children.length,
                  }))
                : undefined;
              const slideLcp = isPrioritySlide ? lcpImagePriority(true, firstBlockBreakpoints) : { priority: 'none' as const };
              // An image paired with a video variant is visible on one side of the 640px breakpoint
              // only — its LCP breakpoints are the slide's, narrowed to that side.
              const sideLcp = (side: readonly Breakpoint[]) => isPrioritySlide
                ? lcpImagePriority(true, (firstBlockBreakpoints ?? ALL_BREAKPOINTS).filter((bp) => side.includes(bp)))
                : { priority: 'none' as const };
              const isSlideSelected = isAdmin && selectedBlockId === child.id;
              const objectFitClass = child.settings?.objectFit === 'contain' ? 'object-contain' : 'object-cover';
              // 'overlay' (default) keeps the existing full-bleed image with text
              // floated on top; 'side-left'/'side-right' instead splits the slide
              // into two columns (image | text) so a portrait image and its caption
              // don't visually collide — see the layout branch below.
              const slideLayout = child.settings?.slideLayout || 'overlay';
              const isSideLayout = slideLayout === 'side-left' || slideLayout === 'side-right';
              const hasSlideChildren = child.children && child.children.length > 0;
              const imageHalfClass = isSideLayout
                ? 'relative w-full h-56 sm:h-full shrink-0 overflow-hidden'
                : 'contents';
              // How much of the row the image column takes on desktop — e.g. a
              // portrait phone photo only needs ~30-40%, leaving the rest for
              // the instructional text instead of a forced 50/50 split.
              const sideImageWidth = Math.min(80, Math.max(20, child.settings?.sideImageWidth ?? 50));
              // In side-by-side layout the image only occupies sideImageWidth% of the row on
              // desktop (see the injected <style> below), not the full viewport — sizes="100vw"
              // regardless of layout told the browser to always fetch a full-width image, wasting
              // bandwidth on every side-left/side-right slide. Full-bleed 'overlay' layout still
              // genuinely spans 100vw, so it keeps that value.
              const imageSizes = isSideLayout ? `(max-width: 639px) 100vw, ${sideImageWidth}vw` : '100vw';
              // Mobile-only stacking order — independent of the desktop left/right
              // choice above, since a merchant may want the caption to read
              // BEFORE the photo on a phone even if the photo sits on the left
              // on desktop (or vice versa).
              const sideMobileTextTop = child.settings?.sideMobileOrder === 'text-top';
              const slideLinkUrl = child.settings?.link || child.settings?.href || child.settings?.cta_url;
              const slideTarget = child.settings?.target || child.settings?.linkTarget || '_self';
              const hasSlideLink = Boolean(slideLinkUrl);
              const SlideWrapper: any = (!isAdmin && hasSlideLink) ? Link : 'div';
              // An image-only slide (text baked into the picture) gives the link no name — screen
              // readers and AI agents saw an empty link. The slide's alt text names it; failing
              // that, its position.
              const slideHasOwnContent = Array.isArray(child.children) && child.children.length > 0;
              const slideLinkLabel = child.settings?.alt
                || (slideHasOwnContent ? undefined : t('slider.slide_link', 'Slajd {n} z {total}')
                  .replace('{n}', String(slideIndex + 1)).replace('{total}', String(block.children.length)));
              const slideWrapperProps = (!isAdmin && hasSlideLink)
                ? {
                    href: slideLinkUrl,
                    target: slideTarget,
                    rel: slideTarget === '_blank' ? 'noopener noreferrer' : undefined,
                    'aria-label': slideLinkLabel,
                  }
                : {};

              return (
                <SlideWrapper
                  key={child.id}
                  {...slideWrapperProps}
                  className={cn(
                    'min-w-full w-full flex-shrink-0 h-full relative overflow-hidden block',
                    isSideLayout && cn(
                      'flex',
                      sideMobileTextTop ? 'flex-col-reverse' : 'flex-col',
                      'sm:flex-row',
                      slideLayout === 'side-right' && 'sm:flex-row-reverse'
                    ),
                    isSlideSelected && 'ring-2 ring-blue-500 ring-inset',
                    hasSlideLink && !isAdmin && 'cursor-pointer'
                  )}
                  style={isFade ? {
                    gridArea: '1 / 1',
                    opacity: isActiveSlide ? 1 : 0,
                    zIndex: isActiveSlide ? 1 : 0,
                    pointerEvents: isActiveSlide ? undefined : 'none',
                    transition: `opacity ${transitionMs}ms ease-in-out`,
                  } : undefined}
                  aria-hidden={isFade && !isActiveSlide ? true : undefined}
                  onClick={(e: React.MouseEvent) => {
                    if (isAdmin && onSelectBlock) {
                      e.stopPropagation();
                      onSelectBlock(child.id);
                    }
                  }}
                >
                {isSideLayout && !isAdmin && (
                  <style>{`
                    @media (min-width: 640px) {
                      .slide-img-${child.id} { width: ${sideImageWidth}%; }
                      .slide-txt-${child.id} { width: ${100 - sideImageWidth}%; }
                    }
                  `}</style>
                )}
                <div
                  className={cn(imageHalfClass, isSideLayout && `slide-img-${child.id}`)}
                  style={isSideLayout && isAdmin ? { width: viewport !== 'mobile' ? `${sideImageWidth}%` : '100%' } : undefined}
                >
                  {/* Optional frame background — letterboxing behind the slide image,
                      visible only where "Zmieść" (contain) leaves gaps. Dedicated
                      settings key, independent of bgImage/mobileBgImage above. */}
                  {(child.settings?.frameBgColor || child.settings?.frameBgImage) && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        backgroundColor: child.settings?.frameBgColor || undefined,
                        backgroundImage: child.settings?.frameBgImage ? `url(${getPublicMediaUrl(child.settings.frameBgImage)})` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    />
                  )}
                  {bgImage || mobileBgImage ? (
                    isAdmin ? (
                      // Admin mode: show only active viewport media
                      isVideoPath(activeBgImage || '') ? (
                        <ResponsiveVideo
                          desktopSrc={getPublicMediaUrl(activeBgImage)}
                          mobileSrc={getPublicMediaUrl(activeBgImage)}
                          desktopPoster={activePoster ? getPublicMediaUrl(activePoster) : undefined}
                          mobilePoster={activePoster ? getPublicMediaUrl(activePoster) : undefined}
                          posterAlt={child.settings?.alt || ''}
                          active={isActiveSlide}
                          loop={!advanceOnVideoEnd}
                          onEnded={handleVideoEnded}
                          forceMobile={viewport === 'mobile'}
                          className={cn("absolute inset-0 w-full h-full pointer-events-none", objectFitClass)}
                          objectPosition={child.settings?.objectPosition || 'center center'}
                          objectFit={child.settings?.objectFit === 'contain' ? 'contain' : 'cover'}
                        />
                      ) : (
                        <SlideBgImage
                          src={activeBgImage}
                          alt={child.settings?.alt}
                          priority={slideLcp.priority}
                          preloadMedia={slideLcp.preloadMedia}
                          sizes={imageSizes}
                          className={cn("absolute inset-0 w-full h-full pointer-events-none", objectFitClass)}
                          style={{ objectPosition: child.settings?.objectPosition || 'center center' }}
                        />
                      )
                    ) : (
                      // Storefront mode: responsive hidden/block elements
                      !mobileBgImage ? (
                        // Fallback: only desktop image exists, show it on all viewports
                        isVideoPath(bgImage || '') ? (
                          <ResponsiveVideo
                            desktopSrc={getPublicMediaUrl(bgImage)}
                            mobileSrc={getPublicMediaUrl(bgImage)}
                            desktopPoster={desktopPoster ? getPublicMediaUrl(desktopPoster) : undefined}
                            mobilePoster={mobilePoster ? getPublicMediaUrl(mobilePoster) : undefined}
                            posterAlt={child.settings?.alt || ''}
                            posterPriority={slideLcp.priority}
                            posterPreloadMedia={slideLcp.preloadMedia}
                            sizes={imageSizes}
                            active={isActiveSlide}
                            loop={!advanceOnVideoEnd}
                            onEnded={handleVideoEnded}
                            className={cn("absolute inset-0 w-full h-full pointer-events-none", objectFitClass)}
                            objectPosition={child.settings?.objectPosition || 'center center'}
                            objectFit={child.settings?.objectFit === 'contain' ? 'contain' : 'cover'}
                          />
                        ) : (
                          <SlideBgImage
                            src={bgImage}
                            alt={child.settings?.alt}
                            priority={slideLcp.priority}
                            preloadMedia={slideLcp.preloadMedia}
                            sizes={imageSizes}
                            className={cn("absolute inset-0 w-full h-full pointer-events-none", objectFitClass)}
                            style={{ objectPosition: child.settings?.objectPosition || 'center center' }}
                          />
                        )
                      ) : (
                        // Both desktop and mobile exist, render both responsively
                        <>
                          {/* Two images: one <picture>, so only the viewport's own variant downloads
                              (two CSS-toggled images used to both download, and the desktop one was
                              lazy on desktop). One image + one video: the image is hidden on the other
                              breakpoint, so it loads 'deferred' — lazy keeps it off the wire there. */}
                          {!isVideoPath(bgImage || '') && !isVideoPath(mobileBgImage) ? (
                            <SlideBgImage
                              src={bgImage}
                              mobileSrc={mobileBgImage}
                              alt={child.settings?.alt}
                              priority={slideLcp.priority}
                              preloadMedia={slideLcp.preloadMedia}
                              sizes={imageSizes}
                              className={cn("absolute inset-0 w-full h-full pointer-events-none", objectFitClass)}
                              style={{ objectPosition: child.settings?.objectPosition || 'center center' }}
                            />
                          ) : (
                            <>
                              {!isVideoPath(bgImage || '') && (
                                <SlideBgImage
                                  src={bgImage}
                                  alt={child.settings?.alt}
                                  {...sideLcp(['tablet', 'desktop'])}
                                  sizes={imageSizes}
                                  className={cn("absolute inset-0 w-full h-full pointer-events-none hidden sm:block", objectFitClass)}
                                  style={{ objectPosition: child.settings?.objectPosition || 'center center' }}
                                />
                              )}
                              {!isVideoPath(mobileBgImage) && (
                                <SlideBgImage
                                  src={mobileBgImage}
                                  alt={child.settings?.alt}
                                  {...sideLcp(['mobile'])}
                                  sizes={imageSizes}
                                  className={cn("absolute inset-0 w-full h-full pointer-events-none block sm:hidden", objectFitClass)}
                                  style={{ objectPosition: child.settings?.objectPosition || 'center center' }}
                                />
                              )}
                            </>
                          )}

                          {/* Videos: render only the right one based on actual viewport */}
                          {(isVideoPath(bgImage || '') || isVideoPath(mobileBgImage)) && (
                            <ResponsiveVideo
                              desktopSrc={isVideoPath(bgImage || '') ? getPublicMediaUrl(bgImage) : ''}
                              mobileSrc={isVideoPath(mobileBgImage) ? getPublicMediaUrl(mobileBgImage) : ''}
                              desktopPoster={desktopPoster ? getPublicMediaUrl(desktopPoster) : undefined}
                              mobilePoster={mobilePoster ? getPublicMediaUrl(mobilePoster) : undefined}
                              posterAlt={child.settings?.alt || ''}
                              posterPriority={slideLcp.priority}
                              posterPreloadMedia={slideLcp.preloadMedia}
                              sizes={imageSizes}
                              active={isActiveSlide}
                              loop={!advanceOnVideoEnd}
                              onEnded={handleVideoEnded}
                              objectPosition={child.settings?.objectPosition || 'center center'}
                              objectFit={child.settings?.objectFit === 'contain' ? 'contain' : 'cover'}
                              className="absolute inset-0 w-full h-full pointer-events-none"
                              forceMobile={isAdmin ? viewport === 'mobile' : undefined}
                            />
                          )}
                        </>
                      )
                    )
                  ) : (
                    <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                      {isAdmin && (
                        <div className="flex flex-col items-center text-white/30 gap-2">
                          <ImageIcon className="w-10 h-10" />
                          <span className="text-[10px] font-bold uppercase tracking-widest">Brak obrazu — ustaw w panelu</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Darkening Overlay — only meaningful over the image, so it's
                      scoped inside the image half in side-by-side layout too. */}
                  {child.settings?.overlayOpacity !== undefined && child.settings.overlayOpacity > 0 && (
                    <div
                      className="absolute inset-0 transition-opacity duration-300 pointer-events-none"
                      style={{
                        backgroundColor: `rgba(0, 0, 0, ${child.settings.overlayOpacity})`,
                        zIndex: 2
                      }}
                    />
                  )}

                  {!isSideLayout && hasSlideChildren && (
                    <div
                      className={cn(
                        'absolute inset-0 flex items-center z-10',
                        isAdmin
                          ? (align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center')
                          : cn(
                              (child.settings?.contentAlign?.mobile || 'center') === 'left' && 'justify-start',
                              (child.settings?.contentAlign?.mobile || 'center') === 'center' && 'justify-center',
                              (child.settings?.contentAlign?.mobile || 'center') === 'right' && 'justify-end',
                              (child.settings?.contentAlign?.desktop || 'center') === 'left' && 'sm:justify-start',
                              (child.settings?.contentAlign?.desktop || 'center') === 'center' && 'sm:justify-center',
                              (child.settings?.contentAlign?.desktop || 'center') === 'right' && 'sm:justify-end'
                            )
                      )}
                      style={child.styles?.padding !== undefined ? { padding: `${child.styles.padding}px` } : undefined}
                    >
                      <BlockRenderer
                        blocks={child.children}
                        viewport={viewport}
                        product={product}
                        selectedBlockId={selectedBlockId}
                        onSelectBlock={onSelectBlock}
                        onUpdateBlockStyle={onUpdateBlockStyle}
                        isAdmin={isAdmin}
                        isBuilderCanvas={isBuilderCanvas}
                        isNested={true}
                        depth={depth + 1}
                        onDuplicateBlock={onDuplicateBlock}
                        onMoveBlockUp={onMoveBlockUp}
                        onMoveBlockDown={onMoveBlockDown}
                        onRemoveBlock={onRemoveBlock}
                      />
                    </div>
                  )}

                  {!isSideLayout && isAdmin && !hasSlideChildren && (
                    <div className="absolute inset-0 flex items-end justify-center pb-4 z-10 opacity-0 hover:opacity-100 transition-opacity">
                      <button
                        className="px-3 py-1.5 bg-white/90 text-gray-800 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg hover:bg-white transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          if ((window as any).__addBlock) {
                            (window as any).__addBlock('slideContent', child.id);
                          }
                        }}
                      >
                        + Dodaj treść na slajd
                      </button>
                    </div>
                  )}
                </div>

                {/* Side-by-side layout: text renders as its own static column next
                    to the image half above, instead of floating over it. */}
                {isSideLayout && (
                  <div
                    className={cn(
                      // flex-1 + min-h-0 (mobile) lets this column claim whatever
                      // height the fixed-height image half didn't use, and
                      // overflow-y-auto means text taller than that space stays
                      // reachable by scrolling instead of being silently clipped
                      // by the slider's own overflow-hidden.
                      'relative w-full flex-1 min-h-0 sm:h-full sm:shrink-0 flex items-center justify-center p-6 sm:p-10 z-10 overflow-y-auto',
                      `slide-txt-${child.id}`
                    )}
                    style={{
                      ...(child.styles?.padding !== undefined ? { padding: `${child.styles.padding}px` } : undefined),
                      ...(isAdmin ? { width: viewport !== 'mobile' ? `${100 - sideImageWidth}%` : '100%' } : undefined),
                    }}
                  >
                    {hasSlideChildren ? (
                      <BlockRenderer
                        blocks={child.children}
                        viewport={viewport}
                        product={product}
                        selectedBlockId={selectedBlockId}
                        onSelectBlock={onSelectBlock}
                        onUpdateBlockStyle={onUpdateBlockStyle}
                        isAdmin={isAdmin}
                        isBuilderCanvas={isBuilderCanvas}
                        isNested={true}
                        depth={depth + 1}
                        onDuplicateBlock={onDuplicateBlock}
                        onMoveBlockUp={onMoveBlockUp}
                        onMoveBlockDown={onMoveBlockDown}
                        onRemoveBlock={onRemoveBlock}
                      />
                    ) : isAdmin ? (
                      <button
                        className="px-3 py-1.5 bg-white text-gray-800 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg hover:bg-gray-50 transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          if ((window as any).__addBlock) {
                            (window as any).__addBlock('slideContent', child.id);
                          }
                        }}
                      >
                        + Dodaj treść na slajd
                      </button>
                    ) : null}
                  </div>
                )}
              </SlideWrapper>
              );
            })}
          </div>

          {hasDots && arrowPosition !== 'below' && renderDots(true)}
          {/* WCAG 2.2.2: anything that moves by itself for over 5 s can be paused. */}
          {!isAdmin && block.settings?.autoplay && (block.children?.length || 0) > 1 && toggleSliderPause && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleSliderPause(block.id); }}
              aria-pressed={!!pausedSliders?.[block.id]}
              aria-label={pausedSliders?.[block.id] ? t('slider.play', 'Wznów przewijanie slajdów') : t('slider.pause', 'Zatrzymaj przewijanie slajdów')}
              className="absolute bottom-3 left-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black/45 text-white hover:bg-black/65"
            >
              {pausedSliders?.[block.id] ? <Play className="h-4 w-4" aria-hidden /> : <Pause className="h-4 w-4" aria-hidden />}
            </button>
          )}
          {hasArrows && arrowPosition === 'overlay' && (
            <>
              {renderArrowButton('prev', true)}
              {renderArrowButton('next', true)}
            </>
          )}
        </div>
        </div>
        {arrowPosition === 'sides' && renderArrowButton('next', false)}
        </div>
      )}

      {/* Below-slider nav row: dots and arrows share one static, centered row
          underneath the photo instead of floating on top of it. */}
      {(block.children?.length || 0) > 0 && arrowPosition === 'below' && (hasDots || hasArrows) && (
        <div className="flex items-center justify-center gap-3 mt-3">
          {hasArrows && renderArrowButton('prev', false)}
          {hasDots && renderDots(false)}
          {hasArrows && renderArrowButton('next', false)}
        </div>
      )}

      {isAdmin && block.children?.length > 0 && (
        <div
          className="absolute -bottom-10 left-1/2 -translate-x-1/2 px-4 py-2 bg-blue-600 text-white rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center shadow-lg cursor-pointer hover:scale-105 transition-all opacity-0 group-hover/slider:opacity-100 z-30"
          onClick={(e) => {
            if (isAdmin && (window as any).__prepareAdd) {
              e.stopPropagation();
              (window as any).__prepareAdd(block.id);
            }
          }}
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          Dodaj Slajd
        </div>
      )}
    </div>
  );
}
